import React, { useState } from 'react'
import { StyleSheet, Text, View } from 'react-native'
import { RadioButton } from '@linqpal/common-frontend/src/ui'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import { TouchableWithoutFeedback } from 'react-native-gesture-handler'
import ExpandableView from '../molecules/ExpandableView'
import moment from 'moment'
import isInteger from 'lodash/isInteger'
import { Trans, useTranslation } from 'react-i18next'
import { Spacer } from './'
import { useResponsive } from '../../utils/hooks'
import { toCurrency } from '@linqpal/models/src/helpers/formatter'
import { Icon } from '@ui-kitten/components'
import RootStore from '../../store/RootStore'
import _ from 'lodash'
import { ILoanPaymentPlanModel } from '@linqpal/models'
import { IDownPaymentDetails } from '@linqpal/models/src/types/routes'
import { BtPlainText } from '@linqpal/components/src/ui'
import { EstimatedDueDatesHint } from '../../screens/Contractor/InvoicePayment/Credit/PaymentPlan/EstimatedDueDatesHint'
import { PaymentPlanDetails } from '../../screens/Contractor/InvoicePayment/Credit/PaymentPlan/PaymentPlanDetails'
import { PaymentPlanHint } from '../../screens/Contractor/InvoicePayment/Credit/PaymentPlan/PaymentPlanHint'
import { DownPaymentMethod } from '@linqpal/models/src/dictionaries/downPayment'

interface IDateWillBeFinalizedProps {
  customStyle?: any
  showInfo?: boolean
}

interface IViewWithDatesProps {
  forSummaryPage?: boolean
  expanded: boolean
  dueDates: Array<string>
}

interface IPaymentPlanCardProps {
  onPress: (
    paymentPlan: ILoanPaymentPlanModel,
    downPayment: IDownPaymentDetails | null,
  ) => void
  paymentOption: any
  paymentAmount: number
  selected: boolean
  downPayment?: IDownPaymentDetails | null
  useDownPayment?: boolean
  forSummaryPage?: boolean
}

const DueDatePill = ({ dueDate }) => {
  return (
    <View
      style={{
        borderRadius: 25,
        backgroundColor: 'rgba(230, 235, 238, 0.45)',
        width: 57,
        height: 20,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 6,
        marginRight: 6,
      }}
    >
      <BtPlainText style={{ fontSize: 11, fontWeight: '500' }}>
        {dueDate}
      </BtPlainText>
    </View>
  )
}

const Fee = ({ paymentOption, selected }) => {
  const { t } = useTranslation('global')

  const months = paymentOption.days / 30
  const monthlyFee = paymentOption.fee / months

  const fee = isInteger(monthlyFee)
    ? monthlyFee
    : isInteger(monthlyFee * 100)
    ? monthlyFee.toFixed(1)
    : monthlyFee.toFixed(2)

  return (
    <View
      style={composeStyle(
        {
          backgroundColor: '#F4F9FD',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 5,
          height: 28,
          paddingVertical: 4,
        },
        selected && { backgroundColor: '#004977' },
      )}
    >
      <Text
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          fontWeight: '700',
          color: selected ? 'white' : '#001929',
          paddingHorizontal: 11,
        }}
      >
        <Trans t={t} i18nKey="PaymentPlanCard.fee" values={{ fee }} />
      </Text>
    </View>
  )
}

function getDueDates(term, days) {
  const dueDates: Array<string> = []
  let paymentDate
  if (term === 1) {
    paymentDate = moment().add(days, 'days')
    dueDates.push(paymentDate.format('MMM DD'))
  }

  for (let i = 0; i < term - 1; i++) {
    if (!i) paymentDate = moment().add(30, 'days')
    else paymentDate = paymentDate.add(1, 'weeks')
    dueDates.push(paymentDate.format('MMM DD'))
  }

  if (term === 5 || term === 9 || term === 13) {
    paymentDate = moment().add(days, 'days')
    dueDates.push(paymentDate.format('MMM DD'))
  }
  return dueDates
}

const Radio = ({ days, selected, testID }) => {
  const { t } = useTranslation('global')

  return (
    <RadioButton
      checked={selected}
      containerStyle={{ paddingVertical: 0 }}
      label={
        <Text
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: '700',
            fontSize: 20,
            lineHeight: 24,
            color: '#001929',
          }}
        >
          {t('PaymentPlanCard.radio-days', { days })}
        </Text>
      }
      radioProps={{
        style: { height: 21, width: 21, borderRadius: 12 },
        innerStyle: { height: 14, width: 14, borderRadius: 8 },
        uncheckedColor: '#99ADBA',
        checkedColor: '#33B3F5',
      }}
      testID={testID}
    />
  )
}

const SampleSchedule = ({ expanded, setExpanded, style = {} }) => {
  const { t } = useTranslation('global')
  return (
    <Text
      onPress={() => setExpanded(!expanded)}
      style={composeStyle(
        {
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          fontWeight: '700',
          lineHeight: 24,
          color: '#00A0F3',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        },
        style,
      )}
    >
      <Trans
        t={t}
        i18nKey="PaymentPlanCard.sample-schedule"
        components={{
          Icon: expanded ? (
            <Icon name="arrow-ios-upward" fill={'#00A0F3'} width={16} />
          ) : (
            <Icon name="arrow-ios-downward" fill={'#00A0F3'} width={16} />
          ),
        }}
      />
    </Text>
  )
}

export const PaymentPlanSelectorFooterMessage: React.FC<IDateWillBeFinalizedProps> =
  ({ customStyle = {} }) => {
    return (
      <View
        style={composeStyle(styles.dateWillBeFinalizedView, customStyle.view)}
      >
        <EstimatedDueDatesHint />
      </View>
    )
  }

const SampleScheduleDates: React.FC<IViewWithDatesProps> = ({
  forSummaryPage,
  expanded,
  dueDates,
}) => {
  return (
    <ExpandableView
      opened={expanded}
      titleStyle={{ fontWeight: '500', color: '#668598', fontSize: 14 }}
      collapsedContainerStyle={{
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
      }}
      collapsableContent={
        <View
          style={{
            backgroundColor: forSummaryPage ? 'none' : '#F8F9F9',
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
            paddingHorizontal: 20,
            paddingVertical: 10,
            maxWidth: 600,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              marginVertical: 5,
            }}
          >
            {dueDates.map((date) => (
              <DueDatePill key={date} dueDate={date} />
            ))}
          </View>
          {!forSummaryPage && <EstimatedDueDatesHint />}
        </View>
      }
    />
  )
}

const PaymentPlanContainer = ({ children, selected, onPress }) => {
  return (
    <View
      style={composeStyle(
        styles.rootView,
        selected && {
          backgroundColor: '#F1FAFF',
          borderColor: '#00A0F3',
          borderWidth: 2,
        },
      )}
    >
      <TouchableWithoutFeedback onPress={() => onPress()}>
        {children}
      </TouchableWithoutFeedback>
    </View>
  )
}

const PaymentPlanOption = ({
  selected,
  onPress,
  paymentOption,
  totalPayment,
  downPayment,
  dueDates,
  weeklyPayments,
}) => {
  const [expanded, setExpanded] = useState(false)
  const { days } = paymentOption
  const { sm } = useResponsive()

  return (
    <PaymentPlanContainer selected={selected} onPress={onPress}>
      <View style={styles.secondaryView}>
        <Radio selected={selected} days={days} testID={'payment-plan-option'} />
        <View
          style={{
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          <Text style={styles.amountStyle}>{toCurrency(totalPayment)}</Text>
        </View>
      </View>
      <View style={sm ? { marginLeft: 28 } : {}}>
        <PaymentPlanDetails
          downPayment={downPayment}
          weeklyPaymentAmount={weeklyPayments}
          paymentOption={paymentOption}
        />
        <View style={[styles.feeWrapper, { paddingHorizontal: sm ? 20 : 16 }]}>
          <Fee paymentOption={paymentOption} selected={selected} />
          <SampleSchedule expanded={expanded} setExpanded={setExpanded} />
        </View>
      </View>
      <SampleScheduleDates dueDates={dueDates} expanded={expanded} />
    </PaymentPlanContainer>
  )
}

const SelectedPaymentPlanSummary = ({
  paymentOption,
  totalPayment,
  downPayment,
  dueDates,
  weeklyPayments,
}) => {
  const { t } = useTranslation('global')
  const [expanded, setExpanded] = useState(false)
  const { sm } = useResponsive()

  return (
    <>
      <View style={styles.summaryWrapper}>
        <View style={styles.secondaryView}>
          <Text style={styles.summaryDaysStyle}>
            {t('PaymentPlanCard.radio-days', { days: paymentOption.days })}
          </Text>
          <View
            style={{
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <Text style={styles.amountStyle}>{toCurrency(totalPayment)}</Text>
          </View>
        </View>
        <View>
          <PaymentPlanDetails
            downPayment={downPayment}
            weeklyPaymentAmount={weeklyPayments}
            paymentOption={paymentOption}
            showCardFee={true}
          />
          <View
            style={[styles.feeWrapper, { paddingHorizontal: sm ? 20 : 16 }]}
          >
            <Fee paymentOption={paymentOption} selected />
            <SampleSchedule expanded={expanded} setExpanded={setExpanded} />
          </View>
        </View>
        <SampleScheduleDates
          dueDates={dueDates}
          expanded={expanded}
          forSummaryPage
        />
        <Spacer />
      </View>

      <EstimatedDueDatesHint />
      {downPayment?.paymentMethod === DownPaymentMethod.Card && (
        <PaymentPlanHint
          text={t('PaymentPlanCard.down-payment-card-fee-hint')}
        />
      )}
    </>
  )
}

export const PaymentPlanCard: React.FC<IPaymentPlanCardProps> = (props) => {
  const { paymentAmount, paymentOption, downPayment, useDownPayment } = props
  const { days, term, fee } = paymentOption

  // TODO: VK: Split summary and option, calculate payment options at the server side and pass them through flow
  // replace current getPlans with getPaymentOptions, return smth. like { plan, downPayment, fee, schedule... }
  // reuse for integration payment as well
  // reuse same backend calculation logic on draw request creation to avoid accepting it as is from UI

  const totalPayment = paymentAmount + Math.round(paymentAmount * fee) / 100

  const downPaymentSettings =
    RootStore.userStore?.company?.settings?.downPaymentDetails

  const downPaymentDetails =
    downPayment ??
    (useDownPayment && downPaymentSettings?.isRequired
      ? {
          percentage: downPaymentSettings.downPaymentPercentage,
          amount: _.round(
            (paymentAmount * downPaymentSettings.downPaymentPercentage) / 100,
            2,
          ),
        }
      : null)

  const drawAmount = totalPayment - (downPaymentDetails?.amount ?? 0)
  const weeklyPayments = Math.round((drawAmount / term) * 100) / 100
  const dueDates = getDueDates(term, days)

  return props.forSummaryPage ? (
    <SelectedPaymentPlanSummary
      paymentOption={paymentOption}
      totalPayment={totalPayment}
      downPayment={downPaymentDetails}
      dueDates={dueDates}
      weeklyPayments={weeklyPayments}
    />
  ) : (
    <PaymentPlanOption
      paymentOption={paymentOption}
      totalPayment={totalPayment}
      downPayment={downPaymentDetails}
      dueDates={dueDates}
      weeklyPayments={weeklyPayments}
      selected={props.selected}
      onPress={() => props.onPress(paymentOption, downPaymentDetails)}
    />
  )
}

const styles = StyleSheet.create({
  rootView: {
    borderWidth: 1,
    borderColor: '#E6EBEE',
    borderRadius: 10,
    justifyContent: 'space-between',
  },
  secondaryView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
  },
  dateWillBeFinalizedView: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  label: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: '500',
    fontSize: 12,
    lineHeight: 22,
    color: '#001929',
  },
  amountStyle: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: '700',
    color: '#001929',
    fontSize: 20,
    lineHeight: 24,
  },
  feeWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  summaryWrapper: {
    backgroundColor: '#F1FAFF',
    marginBottom: 20,
    borderRadius: 10,
  },
  summaryDaysStyle: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: '700',
    fontSize: 20,
    lineHeight: 24,
    color: '#001929',
  },
})
